import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  Calendar,
  Building2,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface OverviewTabProps {
  client: {
    id: string;
    name: string;
    type: 'cfi' | 'psp';
    status: 'prospect' | 'active' | 'inactive';
    createdAt: Date;
    blastTier?: string;
    contractStartDate?: Date;
    contractEndDate?: Date;
  };
}

// Mock KPI Data
const kpiData = {
  annualInvoicing: {
    current: 156000,
    previous: 142000,
    change: 9.86,
  },
  monthlyInvoicing: {
    current: 13000,
    previous: 11833,
    change: 9.86,
  },
  blastUsers: {
    current: 24,
    previous: 21,
    change: 14.29,
  },
  contractDaysRemaining: 287,
};

export function OverviewTab({ client }: OverviewTabProps) {
  const yoyChange = kpiData.annualInvoicing.change;
  const momInvoiceChange = kpiData.monthlyInvoicing.change;
  const momUserChange = kpiData.blastUsers.change;

  return (
    <div className="space-y-6">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Annual Invoicing */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Annual Invoicing</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist mt-1">
                  {formatCurrency(kpiData.annualInvoicing.current)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-turquoise/10 dark:bg-aqua/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-turquoise dark:text-aqua" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              {yoyChange >= 0 ? (
                <TrendingUp className="w-4 h-4 text-turquoise dark:text-aqua" />
              ) : (
                <TrendingDown className="w-4 h-4 text-rust dark:text-salmon" />
              )}
              <span className={cn(
                'text-sm font-medium',
                yoyChange >= 0 ? 'text-turquoise dark:text-aqua' : 'text-rust dark:text-salmon'
              )}>
                {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(1)}%
              </span>
              <span className="text-sm text-chalkboard dark:text-rain-cloud">vs last year</span>
            </div>
          </div>
        </div>

        {/* Monthly Invoicing */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Monthly Invoicing</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist mt-1">
                  {formatCurrency(kpiData.monthlyInvoicing.current)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-pond/10 dark:bg-pond-light/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-pond dark:text-pond-light" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              {momInvoiceChange >= 0 ? (
                <TrendingUp className="w-4 h-4 text-turquoise dark:text-aqua" />
              ) : (
                <TrendingDown className="w-4 h-4 text-rust dark:text-salmon" />
              )}
              <span className={cn(
                'text-sm font-medium',
                momInvoiceChange >= 0 ? 'text-turquoise dark:text-aqua' : 'text-rust dark:text-salmon'
              )}>
                {momInvoiceChange >= 0 ? '+' : ''}{momInvoiceChange.toFixed(1)}%
              </span>
              <span className="text-sm text-chalkboard dark:text-rain-cloud">vs last month</span>
            </div>
          </div>
        </div>

        {/* BLAST Users */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">BLAST Users</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist mt-1">
                  {kpiData.blastUsers.current}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-violet/10 dark:bg-violet-light/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-violet dark:text-violet-light" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              {momUserChange >= 0 ? (
                <TrendingUp className="w-4 h-4 text-turquoise dark:text-aqua" />
              ) : (
                <TrendingDown className="w-4 h-4 text-rust dark:text-salmon" />
              )}
              <span className={cn(
                'text-sm font-medium',
                momUserChange >= 0 ? 'text-turquoise dark:text-aqua' : 'text-rust dark:text-salmon'
              )}>
                {momUserChange >= 0 ? '+' : ''}{momUserChange.toFixed(1)}%
              </span>
              <span className="text-sm text-chalkboard dark:text-rain-cloud">unique logins MoM</span>
            </div>
          </div>
        </div>

        {/* Contract Status */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud">Contract Status</p>
                <p className="text-2xl font-semibold text-slate dark:text-mist mt-1">
                  {kpiData.contractDaysRemaining} days
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-orange/10 dark:bg-orange/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3">
              <Clock className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
              <span className="text-sm text-chalkboard dark:text-rain-cloud">
                Renewal: {client.contractEndDate ? formatDate(client.contractEndDate) : 'Not set'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Details Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Health Card */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate dark:text-mist">Account Health</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                </div>
                <span className="text-sm text-chalkboard dark:text-rain-cloud">Account Type</span>
              </div>
              <span className="badge-primary">{client.type.toUpperCase()}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                </div>
                <span className="text-sm text-chalkboard dark:text-rain-cloud">Status</span>
              </div>
              <span className={cn(
                'badge',
                client.status === 'active' ? 'badge-secondary' : 
                client.status === 'inactive' ? 'badge-warning' : 'badge-neutral'
              )}>
                {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                </div>
                <span className="text-sm text-chalkboard dark:text-rain-cloud">Client Since</span>
              </div>
              <span className="text-sm font-medium text-slate dark:text-mist">
                {formatDate(client.createdAt)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-pale-gray-2 dark:bg-night-pond flex items-center justify-center">
                  <Users className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                </div>
                <span className="text-sm text-chalkboard dark:text-rain-cloud">Total Users (All Time)</span>
              </div>
              <span className="text-sm font-medium text-slate dark:text-mist">42</span>
            </div>
          </div>
        </div>

        {/* Contract Details Card */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-slate dark:text-mist">Contract Details</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Contract Start</span>
              <span className="text-sm font-medium text-slate dark:text-mist">
                {client.contractStartDate ? formatDate(client.contractStartDate) : 'Not set'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Contract End</span>
              <span className="text-sm font-medium text-slate dark:text-mist">
                {client.contractEndDate ? formatDate(client.contractEndDate) : 'Not set'}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Contract Length</span>
              <span className="text-sm font-medium text-slate dark:text-mist">3 Years</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Annual Increase</span>
              <span className="text-sm font-medium text-slate dark:text-mist">CPI (3% Cap)</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray dark:border-night-pond">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Payment Frequency</span>
              <span className="text-sm font-medium text-slate dark:text-mist">Monthly</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-chalkboard dark:text-rain-cloud">Billing Terms</span>
              <span className="text-sm font-medium text-slate dark:text-mist">Net 30</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-slate dark:text-mist">Recent Activity Summary</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-pale-gray-2 dark:bg-night-pond rounded-lg">
              <p className="text-2xl font-semibold text-slate dark:text-mist">12</p>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Communications (30d)</p>
            </div>
            <div className="text-center p-4 bg-pale-gray-2 dark:bg-night-pond rounded-lg">
              <p className="text-2xl font-semibold text-slate dark:text-mist">3</p>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Open Support Tickets</p>
            </div>
            <div className="text-center p-4 bg-pale-gray-2 dark:bg-night-pond rounded-lg">
              <p className="text-2xl font-semibold text-slate dark:text-mist">$13,000</p>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Last Invoice</p>
            </div>
            <div className="text-center p-4 bg-pale-gray-2 dark:bg-night-pond rounded-lg">
              <p className="text-2xl font-semibold text-turquoise dark:text-aqua">Paid</p>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">Invoice Status</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
